import {
  VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH,
  parseStorageUploadDestinations,
  type CompletedStorageUploadPart,
  type ImageMediaDescriptor,
  type MediaDescriptor,
  type MediaRendition,
  type RenditionDescriptor,
  type StorageUploadDestination,
  type VideoMediaDescriptor,
} from "@aasaan-share/protocol";

import { StreamingSha256 } from "./sha256.ts";

const MAX_FILES_PER_UPLOAD = 50;
const MAX_FILE_BYTES = VIDEO_V1_MAX_ORIGINAL_BYTE_LENGTH;
const PREVIEW_MAX_EDGE = 2_048;
const VIDEO_POSTER_MAX_EDGE = 640;
const SUPPORTED_IMAGE_TYPES = new Set([
  "image/avif",
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const SUPPORTED_VIDEO_TYPES = new Set(["video/mp4"]);

export interface BrowserContributionProgress {
  readonly currentItem?: number;
  readonly phase: "finalizing" | "preparing" | "uploading";
  readonly totalItems: number;
}

interface PreparedMedia {
  readonly descriptor: MediaDescriptor;
  readonly display: Blob;
  readonly displayRendition: "preview" | "thumbnail";
  readonly original: File;
}

export async function uploadBrowserContribution(input: {
  readonly connectionToken: string;
  readonly files: readonly File[];
  readonly onProgress?: (progress: BrowserContributionProgress) => void;
  readonly shareId: string;
}): Promise<number> {
  validateFiles(input.files);
  const totalItems = input.files.length;
  const media: PreparedMedia[] = [];
  for (const [index, file] of input.files.entries()) {
    input.onProgress?.({
      currentItem: index + 1,
      phase: "preparing",
      totalItems,
    });
    media.push(await prepareMedia(file));
  }

  const manifest = contributionDelta(media);
  const prepared = await requestJson(
    `/api/shares/${encodeURIComponent(input.shareId)}/storage/uploads`,
    input.connectionToken,
    manifest,
  );
  const destinations = parseStorageUploadDestinations(prepared.uploads);
  const blobs = new Map<string, Blob>();
  for (const item of media) {
    blobs.set(
      uploadKey(item.descriptor.mediaId, item.displayRendition),
      item.display,
    );
    blobs.set(uploadKey(item.descriptor.mediaId, "original"), item.original);
  }

  let currentMediaId: string | null = null;
  let currentItem = 0;
  for (const destination of destinations) {
    if (destination.mediaId !== currentMediaId) {
      currentMediaId = destination.mediaId;
      currentItem += 1;
      input.onProgress?.({
        currentItem,
        phase: "uploading",
        totalItems,
      });
    }
    const blob = blobs.get(uploadKey(
      destination.mediaId,
      destination.rendition,
    ));
    if (!blob || blob.type !== destination.contentType) {
      throw new Error("An upload destination did not match the selected media.");
    }
    await uploadBlobDestination(destination, blob, async (uploadId, parts) => {
      await requestJson(
        `/api/shares/${encodeURIComponent(input.shareId)}/storage/multipart/complete`,
        input.connectionToken,
        {
          manifest: prepared.manifest,
          mediaId: destination.mediaId,
          parts,
          rendition: destination.rendition,
          uploadId,
        },
      );
    });
  }

  input.onProgress?.({ phase: "finalizing", totalItems });
  await requestJson(
    `/api/shares/${encodeURIComponent(input.shareId)}/storage/commit`,
    input.connectionToken,
    prepared.manifest,
  );
  return media.length;
}

function validateFiles(files: readonly File[]): void {
  if (files.length === 0) throw new Error("Choose at least one photo or video.");
  if (files.length > MAX_FILES_PER_UPLOAD) {
    throw new Error(`Choose up to ${MAX_FILES_PER_UPLOAD} items at a time.`);
  }
  for (const file of files) {
    if (
      !SUPPORTED_IMAGE_TYPES.has(file.type)
      && !SUPPORTED_VIDEO_TYPES.has(file.type)
    ) {
      throw new Error("Choose JPEG, PNG, WebP, AVIF, or MP4 files.");
    }
    if (file.size < 1 || file.size > MAX_FILE_BYTES) {
      throw new Error("Each item must be 250 MiB or smaller.");
    }
  }
}

function prepareMedia(file: File): Promise<PreparedMedia> {
  return SUPPORTED_VIDEO_TYPES.has(file.type)
    ? prepareVideo(file)
    : preparePhoto(file);
}

async function preparePhoto(file: File): Promise<PreparedMedia> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`${file.name || "A selected photo"} could not be opened.`);
  }
  try {
    const { canvas, height, width } = scaledCanvas(
      bitmap.width,
      bitmap.height,
      PREVIEW_MAX_EDGE,
    );
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("This browser cannot prepare photo previews.");
    context.drawImage(bitmap, 0, 0, width, height);
    const preview = await canvasBlob(canvas, "image/webp", 0.82);
    const [previewSha256, originalSha256] = await Promise.all([
      sha256(preview),
      sha256(file),
    ]);
    const previewDescriptor: RenditionDescriptor = {
      byteLength: preview.size,
      mimeType: preview.type,
      sha256: previewSha256,
    };
    const descriptor: ImageMediaDescriptor = {
      ...(file.lastModified > 0
        ? { createdAt: new Date(file.lastModified).toISOString() }
        : {}),
      height: bitmap.height,
      kind: "image",
      mediaId: crypto.randomUUID().toLowerCase(),
      renditions: {
        original: {
          byteLength: file.size,
          mimeType: file.type,
          sha256: originalSha256,
        },
        preview: previewDescriptor,
        thumbnail: previewDescriptor,
      },
      width: bitmap.width,
    };
    return {
      descriptor,
      display: preview,
      displayRendition: "preview",
      original: file,
    };
  } finally {
    bitmap.close();
  }
}

async function prepareVideo(file: File): Promise<PreparedMedia> {
  const video = document.createElement("video");
  const sourceUrl = URL.createObjectURL(file);
  video.muted = true;
  video.playsInline = true;
  video.preload = "metadata";
  video.src = sourceUrl;
  try {
    await videoEvent(video, "loadedmetadata");
    if (
      !Number.isFinite(video.duration)
      || video.duration <= 0
      || video.videoWidth < 1
      || video.videoHeight < 1
    ) {
      throw new Error("This MP4 video has invalid metadata.");
    }
    const posterSecond = Math.min(
      Math.max(video.duration * 0.1, 0.05),
      Math.max(0, video.duration - 0.05),
    );
    if (posterSecond > 0) {
      video.currentTime = posterSecond;
      await videoEvent(video, "seeked");
    } else if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await videoEvent(video, "loadeddata");
    }
    const { canvas, height, width } = scaledCanvas(
      video.videoWidth,
      video.videoHeight,
      VIDEO_POSTER_MAX_EDGE,
    );
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("This browser cannot prepare video posters.");
    context.drawImage(video, 0, 0, width, height);
    const poster = await canvasBlob(canvas, "image/jpeg", 0.84);
    const [posterSha256, originalSha256] = await Promise.all([
      sha256(poster),
      sha256(file),
    ]);
    const descriptor: VideoMediaDescriptor = {
      ...(file.lastModified > 0
        ? { createdAt: new Date(file.lastModified).toISOString() }
        : {}),
      durationMs: Math.max(1, Math.round(video.duration * 1_000)),
      height: video.videoHeight,
      kind: "video",
      mediaId: crypto.randomUUID().toLowerCase(),
      originalByteLength: file.size,
      renditions: {
        original: {
          byteLength: file.size,
          mimeType: file.type,
          sha256: originalSha256,
        },
        thumbnail: {
          byteLength: poster.size,
          mimeType: poster.type,
          sha256: posterSha256,
        },
      },
      width: video.videoWidth,
    };
    return {
      descriptor,
      display: poster,
      displayRendition: "thumbnail",
      original: file,
    };
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("This ")) {
      throw error;
    }
    throw new Error(
      `${file.name || "A selected video"} could not be opened. Choose an MP4 video.`,
    );
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(sourceUrl);
  }
}

function contributionDelta(media: readonly PreparedMedia[]) {
  return {
    layout: "stable-v1",
    media: media.map((item) => item.descriptor),
    objects: media.flatMap((item) => {
      const original = item.descriptor.renditions.original!;
      const display = item.displayRendition === "preview"
        && item.descriptor.kind === "image"
        ? item.descriptor.renditions.preview!
        : item.descriptor.renditions.thumbnail;
      return [
        {
          contentIdentity: display.sha256!,
          mediaId: item.descriptor.mediaId,
          rendition: item.displayRendition,
        },
        {
          contentIdentity: original.sha256!,
          mediaId: item.descriptor.mediaId,
          rendition: "original",
        },
      ];
    }),
    operation: "delta",
    removedMediaIds: [],
    revision: crypto.randomUUID().replaceAll("-", "").toLowerCase(),
  };
}

function scaledCanvas(
  sourceWidth: number,
  sourceHeight: number,
  maximumEdge: number,
): { canvas: HTMLCanvasElement; height: number; width: number } {
  const scale = Math.min(1, maximumEdge / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return { canvas, height, width };
}

function canvasBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.type !== mimeType) {
        reject(new Error("This browser could not prepare the selected media."));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });
}

async function sha256(blob: Blob): Promise<string> {
  const digest = new StreamingSha256();
  const reader = blob.stream().getReader();
  while (true) {
    const next = await reader.read();
    if (next.done) return digest.digestHex();
    digest.update(next.value);
  }
}

function videoEvent(
  video: HTMLVideoElement,
  eventName: "loadeddata" | "loadedmetadata" | "seeked",
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener(eventName, resolveEvent);
      video.removeEventListener("error", rejectEvent);
    };
    const resolveEvent = () => {
      cleanup();
      resolve();
    };
    const rejectEvent = () => {
      cleanup();
      reject(new Error("The video could not be decoded."));
    };
    video.addEventListener(eventName, resolveEvent, { once: true });
    video.addEventListener("error", rejectEvent, { once: true });
  });
}

async function uploadBlobDestination(
  destination: StorageUploadDestination,
  blob: Blob,
  completeMultipart: (
    uploadId: string,
    parts: readonly CompletedStorageUploadPart[],
  ) => Promise<void>,
): Promise<void> {
  if (destination.strategy === "single") {
    await uploadWithRetry(destination.url, blob, destination.contentType, false);
    return;
  }
  const expectedPartCount = Math.ceil(blob.size / destination.partSize);
  if (expectedPartCount !== destination.parts.length) {
    throw new Error("The multipart upload destination is invalid.");
  }
  const completed: CompletedStorageUploadPart[] = [];
  for (const part of destination.parts) {
    const start = (part.partNumber - 1) * destination.partSize;
    const end = Math.min(blob.size, start + destination.partSize);
    const etag = await uploadWithRetry(
      part.url,
      blob.slice(start, end),
      "application/octet-stream",
      true,
    );
    completed.push({ etag, partNumber: part.partNumber });
  }
  await completeMultipart(destination.uploadId, completed);
}

async function uploadWithRetry(
  url: string,
  blob: Blob,
  contentType: string,
  requireEtag: true,
): Promise<string>;
async function uploadWithRetry(
  url: string,
  blob: Blob,
  contentType: string,
  requireEtag: false,
): Promise<void>;
async function uploadWithRetry(
  url: string,
  blob: Blob,
  contentType: string,
  requireEtag: boolean,
): Promise<string | void> {
  const delays = [0, 1_000, 2_000, 4_000, 8_000, 15_000, 30_000, 60_000];
  let lastError: unknown;
  for (const delay of delays) {
    if (delay > 0) await wait(delay);
    try {
      const response = await uploadBlob(url, blob, contentType);
      if (!response.ok) {
        throw new Error("A media upload could not be completed.");
      }
      const etag = response.headers.get("etag");
      if (requireEtag && !etag) {
        throw new Error("R2 did not return a multipart ETag.");
      }
      return requireEtag ? etag! : undefined;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("A media upload could not be completed.");
}

async function requestJson(
  path: string,
  connectionToken: string,
  body: unknown,
): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${connectionToken}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  const value = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      isRecord(value) && typeof value.error === "string"
        ? value.error
        : "These items could not be added.",
    );
  }
  if (!isRecord(value)) throw new Error("The upload response is invalid.");
  return value;
}

function uploadBlob(
  url: string,
  blob: Blob,
  contentType: string,
): Promise<Response> {
  return fetch(url, {
    body: blob,
    headers: { "Content-Type": contentType },
    method: "PUT",
  });
}

function uploadKey(mediaId: string, rendition: MediaRendition): string {
  return `${mediaId}:${rendition}`;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
